import { uiText, useUiLanguage } from '../../../utils/uiTranslation';
import React from 'react';
import countries from '../../../utils/countries';

const CompanySection = ({
  canManageCompanySettings,
  companyCoverageLabel,
  companyData,
  companySaving,
  fileInputRef,
  handleInputChange,
  handleLogoChange,
  handleLogoClick,
  handleSave,
  isEditing,
  onEditToggle,
  teamMembers,
  pendingInvites,
  t,
  logoUploading
}) => {
  useUiLanguage();
  const canEditCompany = canManageCompanySettings && isEditing;

  return (
    <div className="profile-section-stack">
    <div className="profile-company-grid">
      <section className="profile-panel profile-panel-main">
        <div className="profile-panel-header">
          <div>
            <h2>{companyData.companyName || t('company.defaultName', 'Company profile')}</h2>
            <p>{companyCoverageLabel}</p>
          </div>
          {canManageCompanySettings && (
            <button type="button" className="profile-btn-secondary" onClick={onEditToggle}>
              {isEditing ? t('cancel', 'Cancel') : t('editProfile', 'Edit profile')}
            </button>
          )}
        </div>

        <div className="profile-company-hero">
          <button
            type="button"
            className="profile-company-logo profile-company-logo--lg"
            onClick={canManageCompanySettings ? handleLogoClick : undefined}
          >
            {companyData.logoUrl ? (
              <img src={companyData.logoUrl} alt={companyData.companyName || uiText("Company")} />
            ) : (
              <span>{(companyData.companyName || 'J').charAt(0).toUpperCase()}</span>
            )}
          </button>

          <div className="profile-company-identity__copy">
            <h3>{companyData.companyName || t('company.defaultName', 'Company profile')}</h3>
            <p>{companyCoverageLabel}</p>
            {canManageCompanySettings && (
              <button type="button" className="profile-inline-link" onClick={handleLogoClick}>
                {logoUploading ? t('company.uploadingLogo', 'Uploading logo...') : t('company.uploadLogo', 'Update company logo')}
              </button>
            )}
          </div>

          <div className="profile-metric-stack">
            <div className="profile-inline-metric">
              <span>{t('company.activeMembers', 'Active members')}</span>
              <strong>{teamMembers.length}</strong>
            </div>
            <div className="profile-inline-metric">
              <span>{t('company.pendingInvites', 'Pending invites')}</span>
              <strong>{pendingInvites.length}</strong>
            </div>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleLogoChange}
            style={{ display: 'none' }}
          />
        </div>

        <div className="profile-field-grid">
          <div className="profile-field">
            <label>{t('company.fieldName', 'Company name')}</label>
            {canEditCompany ? (
              <input
                className="profile-input profile-input--dark"
                type="text"
                value={companyData.companyName}
                onChange={(event) => handleInputChange('companyName', event.target.value)}
                placeholder={t('company.fieldName', 'Company name')}
              />
            ) : (
              <div className="profile-field-value">{companyData.companyName || t('company.notSet', 'Not set')}</div>
            )}
          </div>

          <div className="profile-field">
            <label>{t('company.fieldCountry', 'Country')}</label>
            {canEditCompany ? (
              <select
                className="profile-input profile-input--dark"
                value={companyData.country}
                onChange={(event) => handleInputChange('country', event.target.value)}
              >
                <option value="">{t('selectCountry', 'Select Country')}</option>
                {countries.map((country) => (
                  <option key={country} value={country}>
                    {country}
                  </option>
                ))}
              </select>
            ) : (
              <div className="profile-field-value">{companyData.country || t('company.notSet', 'Not set')}</div>
            )}
          </div>

          <div className="profile-field">
            <label>{t('company.fieldCity', 'City')}</label>
            {canEditCompany ? (
              <input
                className="profile-input profile-input--dark"
                type="text"
                value={companyData.city}
                onChange={(event) => handleInputChange('city', event.target.value)}
                placeholder={t('company.fieldCity', 'City')}
              />
            ) : (
              <div className="profile-field-value">{companyData.city || t('company.notSet', 'Not set')}</div>
            )}
          </div>

          <div className="profile-field">
            <label>{t('company.fieldCoverage', 'Coverage')}</label>
            <div className="profile-field-value">{companyCoverageLabel}</div>
          </div>

          <div className="profile-field profile-field--full">
            <label>{t('company.fieldDescription', 'Company description')}</label>
            {canEditCompany ? (
              <textarea
                className="profile-input profile-input--dark profile-textarea"
                value={companyData.description}
                onChange={(event) => handleInputChange('description', event.target.value)}
                placeholder={t('company.descriptionPlaceholder', 'Add a concise description of the business and how the team operates.')}
              />
            ) : (
              <div className="profile-field-value profile-field-value--multiline">
                {companyData.description || t('company.noDescription', 'No company description has been added yet.')}
              </div>
            )}
          </div>
        </div>

        {canEditCompany && (
          <div className="profile-panel-actions">
            <button type="button" className="profile-btn-primary" onClick={handleSave} disabled={companySaving}>
              {companySaving ? t('company.saving', 'Saving...') : t('save', 'Save')}
            </button>
          </div>
        )}
      </section>
    </div>
  </div>
  );
};

export default CompanySection;
